import {useRef, useState} from 'react';
import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useEffectOnce} from 'react-use';
import {useLocation} from 'react-router-dom';

import icoId from '../../../assets/img/icon/ico_id.png';
import icoPw from '../../../assets/img/icon/ico_pw.png';
import icoPassOff from '../../../assets/img/icon/ico_pass_off.png';
import icoPassOn from '../../../assets/img/icon/ico_pass_on.png';
import Logo from '../../../components/Logo';
import Button from '../../../components/bootstrap/Button';
import Input from '../../../components/bootstrap/forms/Input';
import Label from '../../../components/bootstrap/forms/Label';
import Icon from '../../../components/icon/Icon';
import showNotification from '../../../components/extras/showNotification';
import AuthService from '../../../services/AuthService';
import userService from '../../../services/UserService';
import useAuthenticatedCallback from '../../../hooks/useAuthenticatedCallback';

const SimplePasswordLogin = () => {
    const {t} = useTranslation();
    const location = useLocation();

    const [visiblePassword, setVisiblePassword] = useState(false);
    const [userInfo, setUserInfo] = useState({});
    const isCalledRef = useRef(false);

    const authenticatedCallback = useAuthenticatedCallback();

    const {
        callbackState,
        email
    } = location.state ?? {};

    // 인원정보 가져오기
    const getUserInfo = async (email) => {
        try {
            const result = (
                await userService.getUserInfoNew({
                    email,
                    currentCompanyId: null
                })
            )?.data?.[0];

            if (!result) {
                return showNotification(
                    t('인증하기'),
                    t('해당 이메일로 등록된 사용자 정보가 없습니다.'),
                    'danger'
                );
            }

            setUserInfo(result);
        } catch (error) {
            console.error('인원정보 조회 실패:', error);

            showNotification(
                t('인증하기'),
                t('사용자 정보를 조회하지 못했습니다.'),
                'danger'
            );
        }
    };

    // 첫 진입 시 호출
    useEffectOnce(() => {
        if (isCalledRef.current) return;

        isCalledRef.current = true;

        if (!email) {
            showNotification(
                t('인증하기'),
                t('이메일 정보가 없습니다. 인증을 다시 진행해주세요.'),
                'danger'
            );

            return;
        }

        getUserInfo(email);
    });

    const loginForm = useFormik({
        initialValues: {
            email: userInfo?.loginId || userInfo?.email || '',
            password: ''
        },
        enableReinitialize: true,

        onSubmit: async (values, {setSubmitting}) => {
            try {
                const response = await AuthService.login(values);

                if (!response?.result) {
                    showNotification(
                        t('로그인'),
                        t(response?.message || '아이디 또는 비밀번호를 확인해주세요.'),
                        'danger'
                    );

                    return;
                }

                await authenticatedCallback(callbackState);
            } catch (error) {
                console.error('로그인 실패:', error);

                showNotification(
                    t('로그인'),
                    t('로그인에 실패하였습니다. 다시 확인해주세요.'),
                    'danger'
                );
            } finally {
                setSubmitting(false);
            }
        }
    });

    return (
        <div className='account-login-page'>
            <style>{`
                .account-login-page {
                    position: fixed;
                    z-index: 2000;
                    inset: 0;
                
                    display: flex;
                    align-items: center;
                    justify-content: center;
                
                    padding: 32px;
                
                    background:
                        radial-gradient(
                            circle at 20% 20%,
                            rgba(72, 103, 237, 0.08),
                            transparent 30%
                        ),
                        #f6f7fb;
                }

                .account-login-container {
                    display: grid;
                    grid-template-columns: 0.85fr 1.15fr;

                    width: min(100%, 900px);
                    min-height: 520px;
                    overflow: hidden;

                    border: 1px solid rgba(17, 24, 39, 0.08);
                    border-radius: 28px;

                    background: #fff;

                    box-shadow:
                        0 40px 100px rgba(23, 35, 70, 0.13),
                        0 4px 18px rgba(23, 35, 70, 0.05);
                }

                .account-login-intro {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;

                    padding: 52px 46px;

                    color: #fff;

                    background:
                        linear-gradient(
                            145deg,
                            #18255b 0%,
                            #2746b8 58%,
                            #4168ed 100%
                        );
                }

                .account-login-brand {
                    filter: brightness(0) invert(1);
                }

                .account-login-intro-content h2 {
                    margin: 0 0 18px;

                    font-size: 2.1rem;
                    font-weight: 750;
                    letter-spacing: -0.045em;
                    line-height: 1.3;
                }

                .account-login-intro-content p {
                    margin: 0;

                    color: rgba(255, 255, 255, 0.72);

                    font-size: 0.95rem;
                    line-height: 1.7;
                }

                .account-login-security {
                    display: flex;
                    align-items: center;
                    gap: 8px;

                    color: rgba(255, 255, 255, 0.65);

                    font-size: 0.78rem;
                }

                .account-login-form-area {
                    display: flex;
                    align-items: center;

                    padding: 58px 64px;
                }

                .account-login-form {
                    width: 100%;
                }

                .account-login-eyebrow {
                    margin-bottom: 12px;

                    color: #4665d9;

                    font-size: 0.78rem;
                    font-weight: 750;
                    letter-spacing: 0.12em;
                }

                .account-login-title {
                    margin: 0;

                    color: #131a2c;

                    font-size: 1.8rem;
                    font-weight: 750;
                    letter-spacing: -0.045em;
                    line-height: 1.4;
                }

                .account-login-description {
                    margin: 10px 0 38px;

                    color: #7a8498;

                    font-size: 0.92rem;
                }

                .account-login-form .form-label {
                    margin-bottom: 9px;

                    color: #344054;

                    font-size: 0.84rem;
                    font-weight: 650;
                }

                .account-login-form .id_box,
                .account-login-form .pw_box {
                    position: relative;
                }

                .account-login-form .id_box .form-label,
                .account-login-form .pw_box .form-label {
                    position: absolute;
                    top: 50%;
                    left: 20px;
                    background-repeat: no-repeat;
                    background-position: 0 50%;
                    color: transparent;
                }

                .account-login-form .id_box .form-label {
                    width: 19px;
                    height: 20px;
                    margin-top: -10px;
                }

                .account-login-form .pw_box {
                    margin-top: 10px;
                }

                .account-login-form .pw_box .form-label {
                    width: 17px;
                    height: 24px;
                    margin-top: -12px;
                }

                .account-login-form .id_box .form-control,
                .account-login-form .pw_box .form-control {
                    padding: 0 0 0 50px;
                    font-size: 16px;
                    font-weight: 400;
                    color: #444;
                    line-height: 44px;
                }

                .account-login-form .id_box .form-control::placeholder,
                .account-login-form .pw_box .form-control::placeholder {
                    color: #b1b1b1;
                }

                .account-login-form .id_box .form-control:disabled {
                    background: #f2f4f7;
                    opacity: 1;
                }

                .account-login-form .pw_box .btn_visibility {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    width: 23px;
                    height: 15px;
                    margin-top: -7.5px;
                    background-repeat: no-repeat;
                    background-position: 0 0;
                    padding: 0;
                    border: none;
                    cursor: pointer;
                }

                .account-login-submit {
                    width: 100%;
                    margin-top: 24px;
                    padding: 14px 0;

                    border: 0 !important;
                    border-radius: 10px !important;

                    color: #fff !important;

                    font-size: 16px;
                    font-weight: 600;
                    letter-spacing: 0.02em;

                    background-color: #685af0 !important;

                    box-shadow: 0 4px 14px rgba(104, 90, 240, 0.35);

                    transition: background-color 0.2s, box-shadow 0.2s, transform 0.15s;
                }

                .account-login-submit:not(:disabled):hover {
                    background-color: #5a4de0 !important;
                    box-shadow: 0 6px 18px rgba(104, 90, 240, 0.45);
                    transform: translateY(-1px);
                }

                .account-login-submit:disabled {
                    opacity: 0.5;
                }

                @media (max-width: 767.98px) {
                    .account-login-page {
                        padding: 16px;
                    }

                    .account-login-container {
                        display: block;

                        width: min(100%, 480px);
                        min-height: auto;
                    }

                    .account-login-intro {
                        display: none;
                    }

                    .account-login-form-area {
                        padding: 42px 28px;
                    }
                }
            `}</style>

            <main className='account-login-container'>
                <section className='account-login-intro'>
                    <div className='account-login-brand'>
                        <Logo width={142}/>
                    </div>

                    <div className='account-login-intro-content'>
                        <h2>
                            안전하게,
                            <br/>
                            계속 진행하세요.
                        </h2>

                        <p>
                            현재 계정의 비밀번호를 확인한 후
                            <br/>
                            요청하신 업무를 바로 처리합니다.
                        </p>
                    </div>

                    <div className='account-login-security'>
                        <Icon icon='Lock'/>
                        <span>암호화된 보안 연결</span>
                    </div>
                </section>

                <section className='account-login-form-area'>
                    <form
                        className='account-login-form'
                        onSubmit={loginForm.handleSubmit}
                    >
                        <div className='account-login-eyebrow'>
                            ACCOUNT VERIFICATION
                        </div>

                        <h1 className='account-login-title'>
                            {userInfo?.name}님의 계정정보를
                            <br/>
                            입력해주세요.
                        </h1>

                        <p className='account-login-description'>
                            계속하려면 비밀번호를 확인해주세요.
                        </p>

                        <div className='id_box'>
                            <Label
                                htmlFor='email'
                                style={{backgroundImage: `url(${icoId})`}}
                            >
                                <span className='visually-hidden'>아이디</span>
                            </Label>
                            <Input
                                id='email'
                                name='email'
                                type='text'
                                value={loginForm.values.email}
                                disabled
                            />
                        </div>

                        <div className='pw_box'>
                            <Label
                                htmlFor='password'
                                style={{backgroundImage: `url(${icoPw})`}}
                            >
                                <span className='visually-hidden'>비밀번호</span>
                            </Label>
                            <Input
                                id='password'
                                name='password'
                                type={visiblePassword ? 'text' : 'password'}
                                value={loginForm.values.password}
                                onChange={loginForm.handleChange}
                                placeholder='비밀번호를 입력해주세요'
                                autoComplete='current-password'
                                autoFocus
                            />
                            <Button
                                type='button'
                                className='btn_visibility'
                                style={{
                                    backgroundImage: `url(${visiblePassword ? icoPassOn : icoPassOff})`
                                }}
                                onClick={() =>
                                    setVisiblePassword((visible) => !visible)
                                }
                            />
                        </div>

                        <Button
                            id='login-btn'
                            type='submit'
                            className='w-100 account-login-submit'
                            disabled={
                                loginForm.isSubmitting
                                || !loginForm.values.email
                                || !loginForm.values.password?.trim()
                            }
                        >
                            {
                                loginForm.isSubmitting
                                    ? '확인 중...'
                                    : '계속하기'
                            }
                        </Button>
                    </form>
                </section>
            </main>
        </div>
    );
};

export default SimplePasswordLogin;